import { useMemo, useState, useEffect, useRef } from "react";
import { getWorkspaceTimeZone, getTimeZoneAbbreviation } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { RichTextEditor, type RichTextEditorHandle } from "@/components/rich-text-editor";
import { CcEmailInput } from "@/components/cc-email-input";
import { VoiceToTextInput, VoiceToTextButton } from "@/components/voice-field";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { Mail, Send, Clock, Users, Loader2, ChevronRight, Plus } from "lucide-react";
import type { Lead } from "@shared/schema";
import {
  ColdWarmToggle,
  WarmVolumeAckDialog,
  type MassEmailSettingsLite,
} from "@/components/email/cold-warm-controls";

interface EmailTemplateLite {
  id: string;
  name: string;
  subject?: string;
  body?: string;
  hiddenFromPickers?: boolean;
}

interface DripCampaignLite {
  id: string;
  name: string;
  status: "draft" | "active" | "paused";
  activeEnrollments?: number;
  stepCount?: number;
  riskTier?: "cold" | "warm" | null;
}

interface DripPermissions {
  canEnrollContacts: boolean;
  isAdmin?: boolean;
}

interface LeadCampaignDialogsProps {
  /** Whether the Mass Email vs Drip Campaign chooser is open. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The lead rows currently selected in the spreadsheet. */
  selectedLeads: Lead[];
  /** Email templates already loaded by the parent spreadsheet. */
  emailTemplates: EmailTemplateLite[];
  /** Called after a campaign action completes successfully. */
  onCompleted: () => void;
}

const normalizeEmail = (e?: string | null) => (e || "").toLowerCase().trim();

export function LeadCampaignDialogs({
  open,
  onOpenChange,
  selectedLeads,
  emailTemplates,
  onCompleted,
}: LeadCampaignDialogsProps) {
  const [, navigate] = useLocation();

  const [massEmailOpen, setMassEmailOpen] = useState(false);
  const [dripOpen, setDripOpen] = useState(false);

  // ── Recipient accounting (mirrors the server's dedupe + suppression) ──
  const recipientSummary = useMemo(() => {
    let skippedNoEmail = 0;
    let skippedSuppressed = 0;
    const seen = new Set<string>();
    const validLeadIds: string[] = [];
    const validContacts: { contactId: string; contactType: "lead"; contactEmail: string; contactName?: string }[] = [];

    for (const lead of selectedLeads) {
      const email = normalizeEmail(lead.emailAddress);
      if (!email) {
        skippedNoEmail++;
        continue;
      }
      if (lead.isSuppressed) {
        skippedSuppressed++;
        continue;
      }
      if (seen.has(email)) continue; // dedupe on email
      seen.add(email);
      validLeadIds.push(lead.id);
      validContacts.push({
        contactId: lead.id,
        contactType: "lead",
        contactEmail: email,
        contactName: lead.contactName || lead.leadName || undefined,
      });
    }

    return {
      validCount: validLeadIds.length,
      skippedNoEmail,
      skippedSuppressed,
      validLeadIds,
      validContacts,
    };
  }, [selectedLeads]);

  const handleChoose = (choice: "mass_email" | "drip") => {
    onOpenChange(false);
    if (choice === "mass_email") setMassEmailOpen(true);
    else setDripOpen(true);
  };

  return (
    <>
      {/* Chooser */}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[440px]" data-testid="dialog-campaign-chooser">
          <DialogHeader>
            <DialogTitle>Launch a campaign</DialogTitle>
            <DialogDescription>
              {selectedLeads.length} {selectedLeads.length === 1 ? "lead" : "leads"} selected. Choose how to reach them.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <button
              type="button"
              onClick={() => handleChoose("mass_email")}
              className="w-full flex items-center gap-3 rounded-lg border border-border p-4 text-left hover-elevate active-elevate-2"
              data-testid="button-choose-mass-email"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-md bg-primary/10 text-primary shrink-0">
                <Mail className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium">Mass Email</p>
                <p className="text-sm text-muted-foreground">Send a one-time email to the selected leads now or on a schedule.</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
            </button>
            <button
              type="button"
              onClick={() => handleChoose("drip")}
              className="w-full flex items-center gap-3 rounded-lg border border-border p-4 text-left hover-elevate active-elevate-2"
              data-testid="button-choose-drip"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-md bg-primary/10 text-primary shrink-0">
                <Users className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium">Drip Campaign</p>
                <p className="text-sm text-muted-foreground">Enroll the selected leads into a multi-step automated sequence.</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <MassEmailDialog
        open={massEmailOpen}
        onOpenChange={setMassEmailOpen}
        emailTemplates={emailTemplates}
        summary={recipientSummary}
        onCompleted={() => {
          setMassEmailOpen(false);
          onCompleted();
        }}
      />

      <DripCampaignDialog
        open={dripOpen}
        onOpenChange={setDripOpen}
        summary={recipientSummary}
        onCompleted={() => {
          setDripOpen(false);
          onCompleted();
        }}
        onCreateNew={() => {
          setDripOpen(false);
          navigate("/email-hub?tab=drip-campaign");
        }}
      />
    </>
  );
}

interface RecipientSummary {
  validCount: number;
  skippedNoEmail: number;
  skippedSuppressed: number;
  validLeadIds: string[];
  validContacts: { contactId: string; contactType: "lead"; contactEmail: string; contactName?: string }[];
}

function SkippedNote({ summary }: { summary: RecipientSummary }) {
  const parts: string[] = [];
  if (summary.skippedNoEmail > 0) parts.push(`${summary.skippedNoEmail} missing email`);
  if (summary.skippedSuppressed > 0) parts.push(`${summary.skippedSuppressed} suppressed`);
  if (parts.length === 0) return null;
  return (
    <p className="text-xs text-muted-foreground" data-testid="text-campaign-skipped">
      Skipped: {parts.join(", ")}.
    </p>
  );
}

function MassEmailDialog({
  open,
  onOpenChange,
  emailTemplates,
  summary,
  onCompleted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  emailTemplates: EmailTemplateLite[];
  summary: RecipientSummary;
  onCompleted: () => void;
}) {
  const { toast } = useToast();
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const bodyEditorRef = useRef<RichTextEditorHandle>(null);
  const [cc, setCc] = useState("");
  const [isSending, setIsSending] = useState(false);
  // useRef guard prevents a rapid double-click from firing two concurrent
  // sends before the useState re-render can disable the button.
  const isSendingRef = useRef(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  // Selected-leads is always a prospect/lead audience → default to cold.
  const [riskTier, setRiskTier] = useState<"cold" | "warm">("cold");
  const [warmAckOpen, setWarmAckOpen] = useState(false);
  const [warmAckInfo, setWarmAckInfo] = useState<{ count: number; threshold: number; prospectOverride: boolean } | null>(null);
  // Whether the warm-volume ack dialog was opened by an immediate send or a
  // deferred (scheduled) send — the confirm action branches on this.
  const [warmAckMode, setWarmAckMode] = useState<"send" | "schedule">("send");

  // Admin status (for override lock) + workspace warm-volume settings.
  const { data: perms } = useQuery<{ isAdmin?: boolean }>({
    queryKey: ["/api/drip-campaigns/permissions"],
    enabled: open,
  });
  const isAdmin = perms?.isAdmin === true;
  const { data: meSettings } = useQuery<MassEmailSettingsLite>({
    queryKey: ["/api/mass-email-settings"],
    enabled: open,
  });
  const overrideLocked = !!meSettings?.lockColdOverrideForNonAdmins && !isAdmin;

  useEffect(() => {
    if (!open) {
      setSelectedTemplate("");
      setSubject("");
      setBody("");
      setCc("");
      setScheduleOpen(false);
      setScheduledDate("");
      setScheduledTime("");
      setRiskTier("cold");
      setWarmAckOpen(false);
      setWarmAckInfo(null);
    }
  }, [open]);

  // If the workspace locks overrides for non-admins, force the prospect/lead
  // audience back to cold (server enforces this too — this is just UX).
  useEffect(() => {
    if (overrideLocked && riskTier === "warm") setRiskTier("cold");
  }, [overrideLocked, riskTier]);

  const pickableTemplates = useMemo(
    () => emailTemplates.filter((t) => !t.hiddenFromPickers),
    [emailTemplates],
  );

  const handleTemplateSelect = (templateId: string) => {
    const t = emailTemplates.find((tpl) => tpl.id === templateId);
    if (t) {
      setSubject(t.subject || "");
      setBody(t.body || "");
      setSelectedTemplate(templateId);
    }
  };

  const sendNow = async (opts: { confirmHighBounceRisk?: boolean; confirmWarmHighVolume?: boolean; _isRetry?: boolean } = {}) => {
    const { confirmHighBounceRisk = false, confirmWarmHighVolume = false, _isRetry = false } = opts;
    // Only apply the double-submit guard on the outermost call (button click).
    // Internal recursive confirmations (_isRetry=true) share the same in-flight
    // window, so they must not check or reset the ref themselves.
    if (!_isRetry) {
      if (isSendingRef.current) return;
      isSendingRef.current = true;
      setIsSending(true);
    }
    try {
      const res = await apiRequest("POST", "/api/email/bulk-send", {
        recipients: { audience: ["selected_leads"], leadIds: summary.validLeadIds },
        subject,
        body,
        cc: cc.trim() || undefined,
        riskTier,
        confirmHighBounceRisk,
        confirmWarmHighVolume,
      });
      const result = await res.json();

      if (result.blocked) {
        toast({ title: "Send blocked", description: result.blockedReason || result.message, variant: "destructive" });
        return;
      }
      if (result.requiresWarmVolumeConfirmation && !confirmWarmHighVolume) {
        setWarmAckInfo({
          count: result.totalRecipients ?? summary.validCount,
          threshold: result.warmVolumeThreshold ?? 20,
          prospectOverride: !!result.prospectOverride,
        });
        setWarmAckMode("send");
        setWarmAckOpen(true);
        return;
      }
      if (result.requiresConfirmation && !confirmHighBounceRisk) {
        const pct = result.estimatedBounceRate ? `${(result.estimatedBounceRate * 100).toFixed(1)}%` : "elevated";
        if (window.confirm(`Projected bounce rate is ${pct}. Send anyway?`)) {
          await sendNow({ confirmHighBounceRisk: true, confirmWarmHighVolume, _isRetry: true });
        }
        return;
      }

      const willSendToday = result.willSendToday ?? result.sentCount ?? 0;
      const queuedLater = result.queuedForLater ?? 0;
      const skippedUnverified = result.skippedUnverified ?? 0;
      toast({
        title: "Mass email queued",
        description: `Queued ${result.totalRecipients ?? willSendToday + queuedLater}. Sending ${willSendToday} today${queuedLater > 0 ? `, ${queuedLater} starting next business day` : ""}.${skippedUnverified > 0 ? ` ${skippedUnverified} skipped as undeliverable (verification).` : ""}`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/deliverability/queue-stats"] });
      onCompleted();
    } catch (err: any) {
      toast({ title: "Couldn't send", description: err?.message || "Please try again.", variant: "destructive" });
    } finally {
      // Only the outermost call owns the lock — retries share its finally.
      if (!_isRetry) {
        isSendingRef.current = false;
        setIsSending(false);
      }
    }
  };

  const scheduleSend = async (confirmWarm = false) => {
    if (!scheduledDate || !scheduledTime) return;
    // Build the picker's instant in UTC, interpreting the typed
    // wall-clock as the workspace's configured timezone — same offset
    // dance used by the Mass Email composer in email-hub.tsx.
    const localCT = new Date(`${scheduledDate}T${scheduledTime}:00`);
    const ctParts: Record<string, string> = {};
    for (const p of new Intl.DateTimeFormat("en-US", {
      timeZone: getWorkspaceTimeZone(),
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(localCT)) {
      if (p.type !== "literal") ctParts[p.type] = p.value;
    }
    const ctReadback = new Date(
      `${ctParts.year}-${ctParts.month}-${ctParts.day}T${ctParts.hour}:${ctParts.minute}:${ctParts.second}`,
    );
    const skewMs = localCT.getTime() - ctReadback.getTime();
    const scheduledForUtc = new Date(localCT.getTime() + skewMs);

    // Same ref guard as sendNow — prevents a double-click from scheduling twice.
    if (isSendingRef.current) return;
    isSendingRef.current = true;
    setIsSending(true);
    try {
      const res = await apiRequest("POST", "/api/scheduled-emails", {
        scheduledFor: scheduledForUtc.toISOString(),
        sourceType: "mass_email",
        sourcePayload: {
          recipients: { audience: ["selected_leads"], leadIds: summary.validLeadIds },
          subject,
          body,
          cc: cc.trim() || undefined,
          riskTier,
          ...(confirmWarm ? { confirmWarmHighVolume: true } : {}),
        },
      });
      const result = await res.json();
      if (result?.requiresWarmVolumeConfirmation && !confirmWarm) {
        setWarmAckInfo({
          count: result.totalRecipients ?? summary.validCount,
          threshold: result.warmVolumeThreshold ?? 20,
          prospectOverride: !!result.prospectOverride,
        });
        setWarmAckMode("schedule");
        setWarmAckOpen(true);
        return;
      }
      toast({
        title: "Mass email scheduled",
        description: `Will send ${scheduledDate} at ${scheduledTime} ${getTimeZoneAbbreviation(`${scheduledDate}T${scheduledTime}`)}.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/scheduled-emails"] });
      setWarmAckOpen(false);
      setWarmAckInfo(null);
      onCompleted();
    } catch (err: any) {
      toast({ title: "Couldn't schedule", description: err?.message || "Please try again.", variant: "destructive" });
    } finally {
      isSendingRef.current = false;
      setIsSending(false);
    }
  };

  const canSubmit = !!subject && !!body && summary.validCount > 0 && !isSending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto" data-testid="dialog-mass-email-selected">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Mass Email to selected leads
          </DialogTitle>
          <DialogDescription>
            <span data-testid="text-mass-email-recipient-count">
              {summary.validCount} valid {summary.validCount === 1 ? "recipient" : "recipients"}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <SkippedNote summary={summary} />

          <ColdWarmToggle
            value={riskTier}
            onChange={setRiskTier}
            warmLocked={overrideLocked}
            isLeadAudience={true}
          />

          <div className="space-y-2">
            <Label>Use Template (Optional)</Label>
            <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
              <SelectTrigger data-testid="select-mass-email-template">
                <SelectValue placeholder="Select a template" />
              </SelectTrigger>
              <SelectContent>
                {pickableTemplates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="mass-email-subject">Subject</Label>
            <VoiceToTextInput
              id="mass-email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject..."
              data-testid="input-mass-email-subject"
            />
          </div>

          <CcEmailInput value={cc} onChange={setCc} data-testid="input-mass-email-cc" />

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Message</Label>
              <VoiceToTextButton onTranscript={(t) => bodyEditorRef.current?.insertAtCursor(t)} />
            </div>
            <RichTextEditor
              ref={bodyEditorRef}
              value={body}
              onChange={setBody}
              placeholder="Write your message..."
              minHeight="200px"
              showMergeTokens={true}
              mergeTokens={[
                { token: "{{contact_name}}", label: "Contact Name" },
                { token: "{{account_name}}", label: "Account Name" },
                { token: "{{sender_name}}", label: "Sender Name" },
              ]}
            />
          </div>

          {scheduleOpen && (
            <div className="space-y-3 rounded-lg border border-border p-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="mass-email-schedule-date">Date</Label>
                  <Input
                    id="mass-email-schedule-date"
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    min={new Date().toISOString().split("T")[0]}
                    data-testid="input-mass-email-schedule-date"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="mass-email-schedule-time">Time ({getTimeZoneAbbreviation(scheduledDate && scheduledTime ? `${scheduledDate}T${scheduledTime}` : new Date())})</Label>
                  <Input
                    id="mass-email-schedule-time"
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    data-testid="input-mass-email-schedule-time"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          {scheduleOpen ? (
            <>
              <Button
                variant="outline"
                onClick={() => setScheduleOpen(false)}
                data-testid="button-mass-email-schedule-back"
              >
                Back
              </Button>
              <Button
                onClick={() => scheduleSend(false)}
                disabled={!canSubmit || !scheduledDate || !scheduledTime}
                data-testid="button-mass-email-schedule-confirm"
              >
                {isSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Clock className="w-4 h-4 mr-2" />}
                Schedule Email
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setScheduleOpen(true)}
                disabled={!canSubmit}
                data-testid="button-mass-email-schedule"
              >
                <Clock className="w-4 h-4 mr-2" />
                Schedule Send
              </Button>
              <Button
                onClick={() => sendNow()}
                disabled={!canSubmit}
                data-testid="button-mass-email-send"
              >
                {isSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send Mass Email
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
      {warmAckInfo && (
        <WarmVolumeAckDialog
          open={warmAckOpen}
          onOpenChange={setWarmAckOpen}
          count={warmAckInfo.count}
          threshold={warmAckInfo.threshold}
          prospectOverride={warmAckInfo.prospectOverride}
          isBusy={isSending}
          onConfirm={() => {
            if (warmAckMode === "schedule") {
              void scheduleSend(true);
              return;
            }
            setWarmAckOpen(false);
            void sendNow({ confirmWarmHighVolume: true });
          }}
        />
      )}
    </Dialog>
  );
}

function DripCampaignDialog({
  open,
  onOpenChange,
  summary,
  onCompleted,
  onCreateNew,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  summary: RecipientSummary;
  onCompleted: () => void;
  onCreateNew: () => void;
}) {
  const { toast } = useToast();
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [warmAckOpen, setWarmAckOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedCampaignId("");
      setWarmAckOpen(false);
    }
  }, [open]);

  const { data: campaigns = [], isLoading: campaignsLoading } = useQuery<DripCampaignLite[]>({
    queryKey: ["/api/drip-campaigns"],
    enabled: open,
  });

  const { data: permissions } = useQuery<DripPermissions>({
    queryKey: ["/api/drip-campaigns/permissions"],
    enabled: open,
  });

  const { data: meSettings } = useQuery<MassEmailSettingsLite>({
    queryKey: ["/api/mass-email-settings"],
    enabled: open,
  });
  const warmThreshold = Math.max(1, meSettings?.warmVolumeWarnThreshold ?? 20);

  const activeCampaigns = useMemo(
    () => campaigns.filter((c) => c.status === "active"),
    [campaigns],
  );

  const canEnroll = permissions?.canEnrollContacts !== false;

  const selectedCampaign = useMemo(
    () => campaigns.find((c) => c.id === selectedCampaignId),
    [campaigns, selectedCampaignId],
  );
  // Enrolling a big batch into a WARM campaign means those steps fire from the
  // user's own inbox — gate it behind the deliberate acknowledge modal.
  const enrollNeedsWarmAck =
    selectedCampaign?.riskTier === "warm" && summary.validCount >= warmThreshold;

  const enroll = async () => {
    if (!selectedCampaignId) return;
    if (enrollNeedsWarmAck && !warmAckOpen) {
      setWarmAckOpen(true);
      return;
    }
    setWarmAckOpen(false);
    setIsEnrolling(true);
    try {
      const res = await apiRequest(
        "POST",
        `/api/drip-campaigns/${selectedCampaignId}/enroll`,
        { contacts: summary.validContacts },
      );
      const result = await res.json();
      toast({
        title: "Leads enrolled",
        description: `Enrolled ${result.enrolled ?? 0}${(result.skipped ?? 0) > 0 ? `, skipped ${result.skipped} (already enrolled or suppressed)` : ""}.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/drip-campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["/api/drip-campaigns", selectedCampaignId] });
      onCompleted();
    } catch (err: any) {
      toast({ title: "Couldn't enroll", description: err?.message || "Please try again.", variant: "destructive" });
    } finally {
      setIsEnrolling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]" data-testid="dialog-drip-selected">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Enroll selected leads in a drip
          </DialogTitle>
          <DialogDescription>
            <span data-testid="text-drip-recipient-count">
              {summary.validCount} valid {summary.validCount === 1 ? "lead" : "leads"}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <SkippedNote summary={summary} />

          {!canEnroll ? (
            <p className="text-sm text-muted-foreground" data-testid="text-drip-no-permission">
              You don't have permission to enroll contacts in drip campaigns.
            </p>
          ) : (
            <>
              <div className="space-y-2">
                <Label>Enroll into an active campaign</Label>
                <Select
                  value={selectedCampaignId}
                  onValueChange={setSelectedCampaignId}
                  disabled={campaignsLoading || activeCampaigns.length === 0}
                >
                  <SelectTrigger data-testid="select-drip-campaign">
                    <SelectValue
                      placeholder={
                        campaignsLoading
                          ? "Loading…"
                          : activeCampaigns.length === 0
                            ? "No active campaigns"
                            : "Select a campaign"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {activeCampaigns.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">or</span>
                </div>
              </div>

              <Button
                variant="outline"
                className="w-full"
                onClick={onCreateNew}
                data-testid="button-drip-create-new"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create a new drip campaign
              </Button>
            </>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-drip-cancel">
            Cancel
          </Button>
          <Button
            onClick={enroll}
            disabled={!canEnroll || !selectedCampaignId || summary.validCount === 0 || isEnrolling}
            data-testid="button-drip-enroll"
          >
            {isEnrolling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
            Enroll {summary.validCount} {summary.validCount === 1 ? "lead" : "leads"}
          </Button>
        </DialogFooter>
      </DialogContent>
      <WarmVolumeAckDialog
        open={warmAckOpen}
        onOpenChange={setWarmAckOpen}
        count={summary.validCount}
        threshold={warmThreshold}
        prospectOverride={true}
        isBusy={isEnrolling}
        onConfirm={() => {
          void enroll();
        }}
      />
    </Dialog>
  );
}
